import { api } from "./api";
import { consoleBufferSize, dumpConsoleBuffer } from "@/lib/consoleBuffer";
import { scrubSecrets } from "@/lib/scrubSecrets";
import type { SupportDiagnostics, SupportLogs } from "./support.types";

/**
 * Native support submission (replaces Sentry User Feedback). Gathers the user's
 * diagnostics + on-disk logs (eriol / gstreamer / cef / kero via Electron IPC) + the
 * renderer console buffer, then POSTs a multipart report to the backend. All
 * authenticated (api.authUpload attaches the session AccessKey / CSRF).
 */

export const SUPPORT_SOURCES = [
  "about",
  "menu-bar",
  "error-boundary",
  "mobile",
  "web",
] as const;
export type SupportSource = (typeof SUPPORT_SOURCES)[number];

function coerceSource(source: string): SupportSource {
  return (SUPPORT_SOURCES as readonly string[]).includes(source)
    ? (source as SupportSource)
    : "about";
}

function isElectron(): boolean {
  return typeof window !== "undefined" && Boolean(window.electronAPI);
}

export interface SupportBundle {
  diagnostics: Record<string, unknown>;
  logs: SupportLogs | null;
  rendererLog: string;
}

function browserDiagnostics(): Record<string, unknown> {
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  return {
    appVersion:
      (import.meta.env?.VITE_SENTRY_RELEASE as string | undefined) || "web",
    userAgent: nav?.userAgent ?? "",
    language: nav?.language ?? "",
    platform: nav?.platform ?? "",
    screen:
      typeof screen !== "undefined"
        ? {
            width: screen.width,
            height: screen.height,
            dpr: typeof window !== "undefined" ? window.devicePixelRatio : 1,
          }
        : null,
    collectedAt: new Date().toISOString(),
  };
}

/**
 * Collect everything a report will carry. Fail-safe: any missing piece degrades
 * to empty rather than blocking the submit (a report must always go through).
 */
export async function collectSupportBundle(
  capturedError?: Error,
): Promise<SupportBundle> {
  let diagnostics: Record<string, unknown>;
  let logs: SupportLogs | null = null;

  if (isElectron() && window.electronAPI?.collectSupportDiagnostics) {
    try {
      diagnostics = {
        ...(await window.electronAPI.collectSupportDiagnostics()),
      };
    } catch {
      diagnostics = {};
    }
    if (window.electronAPI.readSupportLogs) {
      try {
        logs = await window.electronAPI.readSupportLogs();
      } catch {
        logs = null;
      }
    }
  } else {
    diagnostics = browserDiagnostics();
  }

  diagnostics.runtime = isElectron() ? "electron" : "browser";
  diagnostics.url = typeof location !== "undefined" ? location.href : "";
  if (capturedError) {
    diagnostics.capturedError = scrubSecrets(
      `${capturedError.name}: ${capturedError.message}${
        capturedError.stack ? `\n${capturedError.stack}` : ""
      }`,
    );
  }

  return { diagnostics, logs, rendererLog: dumpConsoleBuffer() };
}

export interface SubmitSupportReportInput {
  message: string;
  source: string;
  category?: string;
  capturedError?: Error;
  /** Optional user-pasted screenshot, attached as the `screenshot` file. */
  screenshot?: Blob;
}

/** POST the report. Pass a pre-collected bundle (from the preview) to avoid
 *  re-reading the logs. */
export async function submitSupportReport(
  input: SubmitSupportReportInput,
  prebuilt?: SupportBundle,
): Promise<{ id: string }> {
  const bundle = prebuilt ?? (await collectSupportBundle(input.capturedError));
  const fd = new FormData();
  fd.append("message", input.message);
  fd.append("source", coerceSource(input.source));
  if (input.category) fd.append("category", input.category);
  fd.append("diagnostics", JSON.stringify(bundle.diagnostics));

  // Best-effort convenience columns (the full diagnostics JSON has everything).
  const diag = bundle.diagnostics as Partial<SupportDiagnostics> &
    Record<string, unknown>;
  if (typeof diag.appVersion === "string")
    fd.append("appVersion", diag.appVersion.slice(0, 32));
  const platform =
    diag.os?.platform ??
    (typeof diag.platform === "string" ? diag.platform : "");
  if (platform) fd.append("platform", String(platform).slice(0, 16));
  if (diag.os) {
    const osVersion =
      `${diag.os.version || ""} ${diag.os.release || ""}`.trim();
    if (osVersion) fd.append("osVersion", osVersion.slice(0, 64));
    if (diag.os.arch) fd.append("arch", diag.os.arch.slice(0, 16));
  }

  const blob = (text: string) => new Blob([text], { type: "text/plain" });
  if (bundle.logs?.eriolLog)
    fd.append("eriolLog", blob(bundle.logs.eriolLog), "eriol.log");
  if (bundle.logs?.gstreamerLog)
    fd.append("gstreamerLog", blob(bundle.logs.gstreamerLog), "gstreamer.log");
  if (bundle.logs?.cefLog)
    fd.append("cefLog", blob(bundle.logs.cefLog), "cef.log");
  if (bundle.logs?.keroLog)
    fd.append("keroLog", blob(bundle.logs.keroLog), "kero.log");
  if (bundle.rendererLog)
    fd.append("rendererLog", blob(bundle.rendererLog), "renderer.log");
  if (input.screenshot) {
    const ext = (input.screenshot.type?.split("/")[1] || "png").slice(0, 8);
    fd.append("screenshot", input.screenshot, `screenshot.${ext}`);
  }

  return api.authUpload<{ id: string }>("/support/reports", fd);
}

export interface SupportPreviewItem {
  name: string;
  included: boolean;
  sizeLabel: string;
}

/** Human-facing "what we'll send" summary for the consent panel. */
export function buildSupportPreview(bundle: SupportBundle): {
  attachments: SupportPreviewItem[];
  diagnostics: Record<string, unknown>;
} {
  const kb = (s: string) => `${Math.max(1, Math.round(s.length / 1024))} KB`;
  return {
    attachments: [
      {
        name: "eriol.log",
        included: Boolean(bundle.logs?.eriolLog),
        sizeLabel: bundle.logs?.eriolLog ? kb(bundle.logs.eriolLog) : "—",
      },
      {
        name: "gstreamer.log",
        included: Boolean(bundle.logs?.gstreamerLog),
        sizeLabel: bundle.logs?.gstreamerLog
          ? kb(bundle.logs.gstreamerLog)
          : "—",
      },
      {
        name: "cef.log",
        included: Boolean(bundle.logs?.cefLog),
        sizeLabel: bundle.logs?.cefLog ? kb(bundle.logs.cefLog) : "—",
      },
      {
        name: "kero.log",
        included: Boolean(bundle.logs?.keroLog),
        sizeLabel: bundle.logs?.keroLog ? kb(bundle.logs.keroLog) : "—",
      },
      {
        name: "registro del navegador",
        included: Boolean(bundle.rendererLog),
        sizeLabel: `${consoleBufferSize()} líneas`,
      },
    ],
    diagnostics: bundle.diagnostics,
  };
}
